<template>
  <div 
    v-if="visible" 
    class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    @click.self="$emit('close')"
  >
    <div class="bg-white rounded-lg w-full max-w-md max-h-[90vh] overflow-hidden shadow-xl">
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b border-gray-200">
        <div class="flex items-center space-x-3">
          <div class="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <svg class="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787"/>
            </svg>
          </div>
          <div>
            <h3 class="text-lg font-semibold text-gray-900">Share Invoice</h3>
            <p class="text-sm text-gray-600">Send invoice details via WhatsApp</p>
          </div>
        </div>
        <button 
          @click="$emit('close')" 
          class="text-gray-400 hover:text-gray-600 transition-colors p-1"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- Content -->
      <div class="p-4 max-h-[calc(90vh-120px)] overflow-y-auto">
        <!-- Customer Info -->
        <div class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Customer Mobile Number</label>
          <div class="relative">
            <input
              v-model="phoneNumber"
              type="tel"
              placeholder="Enter mobile number"
              class="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-lg"
              :class="{ 'border-red-300': phoneError }"
              @input="validatePhone"
            >
            <div class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
              +91
            </div>
          </div>
          <p v-if="phoneError" class="text-red-500 text-sm mt-1">{{ phoneError }}</p>
          <p v-else-if="phoneNumber" class="text-green-600 text-sm mt-1">✓ Valid mobile number</p>
        </div>

        <!-- Message Preview -->
        <div class="mb-4">
          <div class="flex items-center justify-between mb-2">
            <label class="block text-sm font-medium text-gray-700">Message Preview</label>
            <button
              @click="showFullMessage = !showFullMessage"
              class="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              {{ showFullMessage ? 'Show Less' : 'Show Full Message' }}
            </button>
          </div>
          
          <div class="bg-green-50 border border-green-200 rounded-lg p-3">
            <div class="text-sm text-gray-700 whitespace-pre-line" :class="{ 'line-clamp-4': !showFullMessage }">
              {{ whatsappMessage }}
            </div>
          </div>
        </div>

        <!-- Edit Message Option -->
        <div class="mb-4">
          <label class="flex items-center space-x-2">
            <input
              v-model="enableEdit"
              type="checkbox"
              class="rounded border-gray-300 text-green-600 focus:ring-green-500"
            >
            <span class="text-sm text-gray-700">Edit message before sending</span>
          </label>
        </div>

        <!-- Editable Message -->
        <div v-if="enableEdit" class="mb-4">
          <label class="block text-sm font-medium text-gray-700 mb-2">Custom Message</label>
          <textarea
            v-model="customMessage"
            rows="6"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
            placeholder="Edit your WhatsApp message..."
          ></textarea>
          <p class="text-xs text-gray-500 mt-1">
            Message length: {{ (customMessage || '').length }} characters
          </p>
        </div>

        <!-- Don't show again option -->
        <div class="mb-4">
          <label class="flex items-center space-x-2">
            <input
              v-model="dontShowAgain"
              type="checkbox"
              class="rounded border-gray-300 text-gray-600 focus:ring-gray-500"
            >
            <span class="text-sm text-gray-700">Don't show this popup for future sales</span>
          </label>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-between p-4 bg-gray-50 border-t border-gray-200">
        <button
          @click="handleSkip"
          class="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
        >
          Skip
        </button>
        <div class="flex space-x-3">
          <button
            @click="$emit('close')"
            class="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            @click="handleSend"
            :disabled="!isValidPhone || sending"
            class="flex items-center space-x-2 px-6 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <svg v-if="sending" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
            </svg>
            <svg v-else class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787"/>
            </svg>
            <span>
              {{ sending ? 'Opening...' : 'Send WhatsApp' }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { 
  formatPhoneForWhatsApp, 
  generateWhatsAppMessage, 
  openWhatsApp 
} from '@/utils/whatsappUtils'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  transaction: {
    type: Object,
    required: false,
    default: null
  },
  business: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['close', 'skip', 'sent', 'dont-show-again'])

// Reactive data
const phoneNumber = ref('')
const phoneError = ref('')
const showFullMessage = ref(false)
const enableEdit = ref(false)
const customMessage = ref('')
const dontShowAgain = ref(false)
const sending = ref(false)


// Computed properties
const whatsappMessage = computed(() => {
  if (!props.transaction || !props.business) {
    return ''
  }
  
  let message = enableEdit.value && customMessage.value 
    ? customMessage.value 
    : generateWhatsAppMessage(props.transaction, props.business)
  

  
  return message
})

const isValidPhone = computed(() => {
  return phoneNumber.value && !phoneError.value
})

// Methods
const validatePhone = () => {
  phoneError.value = ''
  
  if (!phoneNumber.value) {
    phoneError.value = 'Mobile number is required'
    return
  }
  
  const formatted = formatPhoneForWhatsApp(phoneNumber.value)
  if (!formatted) {
    phoneError.value = 'Please enter a valid 10-digit mobile number'
  }
}

const handleSend = async () => {
  if (!isValidPhone.value) {
    validatePhone()
    return
  }
  
  sending.value = true
  
  try {
    const formattedPhone = formatPhoneForWhatsApp(phoneNumber.value)
    
    if (!props.transaction || !props.business) {
      throw new Error('Transaction or business data is missing')
    }
    
    let message = enableEdit.value && customMessage.value 
      ? customMessage.value 
      : generateWhatsAppMessage(props.transaction, props.business)
    

    
    // Open WhatsApp
    openWhatsApp(formattedPhone, message)
    
    // Small delay for better UX
    await new Promise(resolve => setTimeout(resolve, 500))
    
    // Emit events
    emit('sent', {
      phone: formattedPhone,
      message: message,

      dontShowAgain: dontShowAgain.value
    })
    
    emit('close')
  } catch (error) {
    console.error('Error opening WhatsApp:', error)
  } finally {
    sending.value = false
  }
}

const handleSkip = () => {
  emit('skip', {
    dontShowAgain: dontShowAgain.value
  })
  emit('close')
}

// Watch for changes
watch(() => props.visible, (newVisible) => {
  if (newVisible && props.transaction) {
    // Pre-fill phone number from transaction
    phoneNumber.value = props.transaction.customer_mobile || ''
    customMessage.value = generateWhatsAppMessage(props.transaction, props.business)
    validatePhone()
  }
})

// Initialize
onMounted(() => {
  if (props.visible && props.transaction && props.transaction.customer_mobile) {
    phoneNumber.value = props.transaction.customer_mobile
    validatePhone()
  }
})
</script>

<style scoped>
.line-clamp-4 {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>